import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
function Pagination({
  totalPages,
  pagesToShow,
  currentPage,
  onPageChange,
  nextPageLabel,
  previousPageLabel,
  pageLabel
}) {
  const pages =
    pagesToShow || Object.keys([...Array(totalPages)]).map(i => Number(i) + 1);
  const paginationRef = useRef(null);
  const [parentSection, setParentSection] = useState();

  const scrollToTop = () => {
    if (parentSection) {
      setTimeout(() => {
        parentSection.scrollIntoView({ behavior: 'smooth' });
      }, 250);
    }
  };

  const handlePageChange = event => {
    const page = Number(event.currentTarget.value);
    event.currentTarget.blur();
    onPageChange(page);
    scrollToTop();

    const global = window;

    global.dataLayer?.push({
      event: 'pagination_click',
      event_category: 'pagination',
      event_action: 'click',
      event_label: page
    });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
      scrollToTop();

      const global = window;

      global.dataLayer?.push({
        event: 'pagination_click',
        event_category: 'pagination',
        event_action: 'click',
        event_label: 'previous'
      });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
      scrollToTop();

      const global = window;

      global.dataLayer?.push({
        event: 'pagination_click',
        event_category: 'pagination',
        event_action: 'click',
        event_label: 'next'
      });
    }
  };

  const renderPage = (pageNumber, active) => {
    return (
      <li>
        <button
          className={`pagination__page ${active ? 'active' : ''}`}
          value={pageNumber}
          onClick={handlePageChange}
          aria-label={pageNumber.toString()}
        >
          <span className="sr-only">{pageLabel} </span>
          {pageNumber}
        </button>
      </li>
    );
  };

  const renderEllipsis = () => (
    <li>
      <span role="presentation" className="pagination__ellipsis">
        {'...'}
      </span>
    </li>
  );

  useEffect(() => {
    if (paginationRef.current) {
      const parentSection = paginationRef.current.closest('section');
      parentSection?.style.setProperty(
        'scroll-margin-block-start',
        'calc(var(--header-height) + 100px)'
      );
      setParentSection(parentSection);
    }
  }, [paginationRef.current]);

  if (totalPages > 1) {
    return (
      <ul className="pagination" ref={paginationRef}>
        <li>
          <button
            className="pagination__prev"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            title={previousPageLabel}
            aria-label={previousPageLabel}
          >
            <svg
              width="8"
              height="13"
              viewBox="0 0 8 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.399.174 8 1.734 3.381 6.476 8 11.25l-1.611 1.55L.272 6.466 6.399.174z"
                fill="currentColor"
              />
            </svg>
          </button>
        </li>

        {renderPage(1, currentPage === 1)}

        {currentPage > 4 && totalPages > 5 && <>{renderEllipsis()}</>}

        {totalPages <= 5 &&
          pages.map((page, index) => (
            <React.Fragment key={index}>
              {page > 1 && page < totalPages && (
                <>{renderPage(page, currentPage === page)}</>
              )}
            </React.Fragment>
          ))}

        {totalPages > 5 &&
          pages.map((page, index) => {
            if (currentPage <= 4 && page <= 4 && page > 1) {
              return (
                <React.Fragment key={index}>
                  {renderPage(page, currentPage === page)}
                </React.Fragment>
              );
            }

            if (
              currentPage > 4 &&
              page >= currentPage - 1 &&
              page <= currentPage + 1 &&
              page < totalPages
            ) {
              return (
                <React.Fragment key={index}>
                  {renderPage(page, currentPage === page)}
                </React.Fragment>
              );
            }

            if (
              currentPage >= totalPages - 1 &&
              page >= totalPages - 3 &&
              page < totalPages
            ) {
              return (
                <React.Fragment key={index}>
                  {renderPage(page, currentPage === page)}
                </React.Fragment>
              );
            }
            return null;
          })}

        {totalPages === 6 && currentPage + 2 <= totalPages && (
          <>{renderEllipsis()}</>
        )}

        {totalPages > 6 && currentPage + 3 <= totalPages && (
          <>{renderEllipsis()}</>
        )}

        {renderPage(totalPages, currentPage === totalPages)}
        <li>
          <button
            onClick={handleNextPage}
            className="pagination__next"
            disabled={currentPage === totalPages}
            title={nextPageLabel}
            aria-label={nextPageLabel}
          >
            <svg
              width="8"
              height="13"
              viewBox="0 0 8 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="m1.873 12.624-1.601-1.56L4.89 6.322.272 1.55 1.883 0 8 6.333l-6.127 6.291z"
                fill="currentColor"
              />
            </svg>
          </button>
        </li>
      </ul>
    );
  }

  return null;
}

Pagination.propTypes = {
  totalPages: PropTypes.number.isRequired,
  pagesToShow: PropTypes.array,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  nextPageLabel: PropTypes.string.isRequired,
  previousPageLabel: PropTypes.string.isRequired,
  pageLabel: PropTypes.string.isRequired
};

export default Pagination;
